import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, MoreHorizontal, Pause, Pencil, Play, Plus, RefreshCw, RotateCw, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { errorText } from '../../lib/apiErrors';
import { formatDate, listDeliveries, listWebhooks, relativeTime, resendDelivery, webhookAction } from '../../lib/developerApi';
import { Note, StatusCode, formatLogTime } from './parts';
import { EndpointDialog, RemoveEndpointDialog, SecretDialog } from './WebhookDialogs';

function HealthPill({ endpoint }) {
    if (endpoint.health === 'paused') {
        return (
            <span>
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-stone-100 text-stone-500">Paused</span>
                {endpoint.paused_reason === 'failing' && <span className="block text-xs text-stone-400 mt-0.5">after 24 h of failures</span>}
            </span>
        );
    }
    if (endpoint.health === 'failing') {
        return (
            <span>
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700">Failing</span>
                <span className="block text-xs text-stone-400 mt-0.5">{endpoint.consecutive_failures} failed in a row</span>
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Healthy
        </span>
    );
}

function DeliveryResult({ delivery }) {
    if (delivery.response_status) return <StatusCode status={delivery.response_status} />;
    if (delivery.status === 'pending' || delivery.status === 'in_flight') return <span className="text-xs text-stone-400">Queued</span>;
    if (delivery.status === 'paused') return <span className="text-xs text-stone-500">Waiting (paused)</span>;
    if (delivery.status === 'cancelled') return <span className="text-xs text-stone-400">Cancelled</span>;
    return <span className="text-xs text-red-700" title={delivery.error || ''}>{delivery.error || 'Failed'}</span>;
}

/**
 * Admin → Developer API → Webhooks (#793 S7).
 *
 * Org webhook endpoints — URL, events, health, last delivery — and the most
 * recent deliveries across them. Add, edit, test, pause/resume, rotate the
 * signing secret and remove; a failed delivery can be resent.
 */
export default function WebhooksTab({ headers, onCount }) {
    const [state, setState] = useState({ endpoints: [], events: [], max: 10, signingAvailable: true });
    const [deliveries, setDeliveries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null); // {mode: 'add'} | {mode: 'edit', endpoint}
    const [secret, setSecret] = useState(null); // {value, rotated}
    const [removing, setRemoving] = useState(null);
    const [busy, setBusy] = useState(null);

    const reload = useCallback(async () => {
        try {
            const [hooks, recent] = await Promise.all([listWebhooks(headers), listDeliveries(headers, { limit: 25 })]);
            setState(hooks);
            setDeliveries(recent);
            if (onCount) onCount(hooks.endpoints.length);
        } catch (err) {
            toast.error(errorText(err, 'Could not load webhook endpoints.'));
        } finally {
            setLoading(false);
        }
    }, [headers, onCount]);

    useEffect(() => {
        reload();
    }, [reload]);

    const act = async (endpoint, action) => {
        setBusy(endpoint.endpoint_id);
        try {
            const out = await webhookAction(headers, endpoint.endpoint_id, action);
            if (action === 'test') {
                const d = out.delivery || {};
                if (d.status === 'delivered') toast.success(`Test event delivered (${d.response_status}, ${d.duration_ms} ms)`);
                else toast.error(`Test event failed: ${d.response_status ? `HTTP ${d.response_status}` : (d.error || 'no response')}`);
            } else if (action === 'rotate-secret') {
                setSecret({ value: out.signing_secret, rotated: true });
            } else if (action === 'pause') {
                toast.success('Deliveries paused. Events are kept for 7 days.');
            } else if (action === 'resume') {
                toast.success(out.requeued ? `Deliveries resumed; ${out.requeued} waiting events will be sent.` : 'Deliveries resumed.');
            }
            await reload();
        } catch (err) {
            toast.error(errorText(err, 'That did not work.'));
        } finally {
            setBusy(null);
        }
    };

    const resend = async (delivery) => {
        try {
            await resendDelivery(headers, delivery.delivery_id);
            toast.success('Delivery queued again');
            await reload();
        } catch (err) {
            toast.error(errorText(err, 'Could not resend the delivery.'));
        }
    };

    const urlOf = (endpointId) => (state.endpoints.find((e) => e.endpoint_id === endpointId)?.url || '(removed endpoint)').replace(/^https:\/\//, '');
    const full = state.endpoints.length >= state.max;
    const anyFailing = state.endpoints.some((e) => e.health === 'failing' || e.paused_reason === 'failing');

    if (loading) {
        return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>;
    }

    return (
        <div className="flex flex-col gap-5" data-testid="webhooks-tab">
            <div className="flex items-start gap-3">
                <p className="flex-1 m-0 text-sm text-stone-600 max-w-[70ch]">
                    Tell other apps when something changes in your CRM. Each endpoint gets signed events, retried for about a day if it’s down.
                </p>
                <button
                    type="button"
                    disabled={full || !state.signingAvailable}
                    onClick={() => setDialog({ mode: 'add' })}
                    data-testid="add-endpoint"
                    title={full ? `At most ${state.max} endpoints` : undefined}
                    className="h-8 px-3 rounded-md bg-stone-900 text-white text-[13px] font-medium inline-flex items-center gap-1.5 hover:bg-stone-800 disabled:bg-stone-200 disabled:text-stone-400"
                >
                    <Plus className="w-4 h-4" /> Add endpoint
                </button>
            </div>

            {!state.signingAvailable && (
                <Note icon={AlertTriangle} title="Webhook signing is not set up on this workspace">
                    Endpoints can be added once the workspace’s encryption key is configured. Contact support.
                </Note>
            )}

            {state.endpoints.length ? (
                <div className="border border-stone-200 rounded-xl overflow-x-auto">
                    <table className="w-full min-w-[720px]">
                        <thead>
                            <tr className="text-left text-xs font-medium tracking-wider uppercase text-stone-500 border-b border-stone-200">
                                <th className="px-4 py-3">Endpoint</th>
                                <th className="px-4 py-3">Events</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Last delivery</th>
                                <th className="px-4 py-3 w-12" />
                            </tr>
                        </thead>
                        <tbody>
                            {state.endpoints.map((e) => (
                                <tr key={e.endpoint_id} data-testid="endpoint-row" className="border-b border-stone-100 last:border-b-0 hover:bg-stone-50 align-top">
                                    <td className="px-4 py-3 max-w-[340px]">
                                        <span className="font-mono text-[12.5px] break-all text-stone-900">{e.url}</span>
                                        <span className="block text-xs text-stone-400 mt-0.5">
                                            {e.description ? `${e.description} · ` : ''}Added {formatDate(e.created_at)}
                                            {e.verification === 'verified_echo' ? ' · verified' : ''}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 max-w-[280px]">
                                        {e.events.map((ev) => (
                                            <span key={ev} className="inline-flex font-mono text-[11px] font-medium text-stone-600 bg-stone-100 rounded px-1.5 py-0.5 mr-1 my-0.5">{ev}</span>
                                        ))}
                                    </td>
                                    <td className="px-4 py-3"><HealthPill endpoint={e} /></td>
                                    <td className="px-4 py-3 text-[13px] whitespace-nowrap text-stone-600">
                                        {e.last_delivery_at ? `${relativeTime(e.last_delivery_at)} · ${e.last_status || e.last_error || '—'}` : 'No deliveries yet'}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <button type="button" disabled={busy === e.endpoint_id} aria-label={`Actions for ${e.url}`} className="w-8 h-8 rounded-md inline-flex items-center justify-center text-stone-500 hover:bg-stone-100">
                                                    {busy === e.endpoint_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreHorizontal className="w-4 h-4" />}
                                                </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => act(e, 'test')}><Send className="w-4 h-4" /> Send test event</DropdownMenuItem>
                                                {e.health === 'paused'
                                                    ? <DropdownMenuItem onClick={() => act(e, 'resume')}><Play className="w-4 h-4" /> Resume deliveries</DropdownMenuItem>
                                                    : <DropdownMenuItem onClick={() => act(e, 'pause')}><Pause className="w-4 h-4" /> Pause deliveries</DropdownMenuItem>}
                                                <DropdownMenuItem onClick={() => setDialog({ mode: 'edit', endpoint: e })}><Pencil className="w-4 h-4" /> Edit endpoint</DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => act(e, 'rotate-secret')}><RotateCw className="w-4 h-4" /> Rotate signing secret</DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem className="text-red-600" onClick={() => setRemoving(e)}><Trash2 className="w-4 h-4" /> Remove endpoint</DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="border border-dashed border-stone-200 rounded-xl bg-stone-50 p-9 text-center text-sm text-stone-500" data-testid="webhooks-empty">
                    No endpoints yet. Add one to tell another app when contacts or teammates change.
                </div>
            )}

            <div>
                <div className="flex items-center mb-2.5">
                    <h3 className="flex-1 m-0 text-[15px] font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Recent deliveries</h3>
                    <button type="button" onClick={reload} className="h-7 px-2.5 rounded-md border border-stone-200 text-[12.5px] font-medium inline-flex items-center gap-1.5 hover:bg-stone-50">
                        <RefreshCw className="w-3 h-3" /> Refresh
                    </button>
                </div>
                {deliveries.length ? (
                    <div className="border border-stone-200 rounded-xl overflow-x-auto">
                        <table className="w-full min-w-[640px]">
                            <thead>
                                <tr className="text-left text-xs font-medium tracking-wider uppercase text-stone-500 border-b border-stone-200">
                                    <th className="px-4 py-3">Time</th>
                                    <th className="px-4 py-3">Event</th>
                                    <th className="px-4 py-3">Endpoint</th>
                                    <th className="px-4 py-3">Response</th>
                                    <th className="px-4 py-3 text-right">Duration</th>
                                    <th className="px-4 py-3 w-20" />
                                </tr>
                            </thead>
                            <tbody>
                                {deliveries.map((d) => (
                                    <tr key={d.delivery_id} data-testid="delivery-row" className="border-b border-stone-100 last:border-b-0">
                                        <td className="px-4 py-2.5 text-[13px] text-stone-500 tabular-nums whitespace-nowrap">{formatLogTime(d.created_at)}</td>
                                        <td className="px-4 py-2.5">
                                            <span className="inline-flex font-mono text-[11px] font-medium text-stone-600 bg-stone-100 rounded px-1.5 py-0.5">{d.event_type}</span>
                                            {d.attempt > 1 && <span className="ml-1.5 text-[11px] text-stone-400">attempt {d.attempt}</span>}
                                        </td>
                                        <td className="px-4 py-2.5 font-mono text-xs text-stone-600 break-all">{urlOf(d.endpoint_id)}</td>
                                        <td className="px-4 py-2.5 whitespace-nowrap"><DeliveryResult delivery={d} /></td>
                                        <td className="px-4 py-2.5 text-right text-[13px] text-stone-500 tabular-nums">{d.duration_ms != null ? `${d.duration_ms} ms` : '—'}</td>
                                        <td className="px-4 py-2.5 text-right">
                                            {(d.status === 'failed' || d.status === 'paused') && (
                                                <button type="button" onClick={() => resend(d)} data-testid="delivery-resend" className="h-7 px-2.5 rounded-md border border-stone-200 text-[12px] font-medium hover:bg-stone-50">
                                                    Resend
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="border border-dashed border-stone-200 rounded-xl bg-stone-50 p-6 text-center text-sm text-stone-500">
                        No deliveries in the last 7 days.
                    </div>
                )}
            </div>

            {anyFailing && (
                <Note icon={AlertTriangle} title="A failing endpoint is paused after 24 hours">
                    Undelivered events are kept for 7 days, so they can be resent once the endpoint is fixed.
                </Note>
            )}

            <EndpointDialog
                open={Boolean(dialog)}
                endpoint={dialog?.mode === 'edit' ? dialog.endpoint : null}
                catalog={state.events}
                headers={headers}
                onClose={() => setDialog(null)}
                onCreated={(created) => {
                    setDialog(null);
                    setSecret({ value: created.signing_secret, rotated: false });
                    reload();
                }}
                onSaved={() => {
                    setDialog(null);
                    toast.success('Endpoint saved');
                    reload();
                }}
            />
            <SecretDialog
                secret={secret?.value}
                rotated={secret?.rotated}
                onClose={() => {
                    setSecret(null);
                    toast.success(secret?.rotated ? 'Secret rotated' : 'Endpoint added. Send it a test event from its menu.');
                }}
            />
            <RemoveEndpointDialog
                endpoint={removing}
                headers={headers}
                onClose={() => setRemoving(null)}
                onRemoved={() => {
                    setRemoving(null);
                    reload();
                }}
            />
        </div>
    );
}
